import * as React from 'react'
import EnvironmentSelector from '@mulesoft/monitoring-center-components/lib/components/Selector/EnvironmentSelector/EnvironmentSelector'
import RecipientsSelector from '@mulesoft/monitoring-center-components/lib/components/Selector/RecipientsSelector/RecipientsSelector'
import ResourceSelector from '@mulesoft/monitoring-center-components/lib/components/Selector/ResourceSelector/ResourceSelector'
import SeveritySelector from '@mulesoft/monitoring-center-components/lib/components/Selector/SeveritySelector/SeveritySelector'
import * as labels from '@mulesoft/monitoring-center-components/lib/utils/labels'
import RadioGroup from '@mulesoft/anypoint-components/lib/RadioGroup'
import TextField from '@mulesoft/anypoint-components/lib/TextField'
import TextArea from '@mulesoft/anypoint-components/lib/TextArea'
import Select from '@mulesoft/anypoint-components/lib/Select'
import Button from '@mulesoft/anypoint-components/lib/Button'
import Radio from '@mulesoft/anypoint-components/lib/Radio'
import Form from '@mulesoft/anypoint-components/lib/Form'
import {
  AlertResourceDeploymentType,
  getAlertResourceDeploymentType,
  getClusterId,
  getInfluxResourceId,
  getResourceId,
  isApplicationResource,
  isAsset,
  transformToResourceSelectorProps
} from '../../../../../../common/resource/model'
import {
  AlertResource,
  AlertResourceType,
  BasicAlert,
  BasicAlertConditionFields,
  basicAlertMetricsMap,
  BasicAlertsMetricType,
  EmailNotification,
  getAlertNotification,
  getAlertProperty,
  getDefaultAlertResource,
  getMetricThreshold,
  setAlertCondition
} from '../../../../model'
import { BasicSelectorOption } from '@mulesoft/monitoring-center-components/lib/components/Selector/BasicSelector/BasicSelector'
import { AlertSeverity } from '@mulesoft/monitoring-center-components/lib/components/Selector/SeveritySelector/model'
import { transformToEnvironmentSelectorProps } from '../../../../../../common/environment/model'
import { HybridSettingsPage } from '../../../../../Settings/Body/Hybrid/HybridSettingsPage'
import { ResourceType } from '@mulesoft/monitoring-center-components/lib/models/resource'
import { EnvironmentSelectorState } from '../../../../../../common/environment/reducer'
import { ResourceSelectorState } from '../../../../../../common/resource/reducer'
import { VersionSelectorState } from '../../../../../../common/version/reducer'
import { Profile } from '../../../../../../remote-api/CoreServicesRemoteApi'
import { getSelectedRecipients, getUserEmailOption } from '../../model'
import { ARMServer, User } from '../../../../../../remote-api/models'
import { SelectorResource } from '@mulesoft/monitoring-center-components/lib/models/selector'
import { transformToVersionSelectorProps } from '../../../../../../common/version/model'
import VersionSelector from '@mulesoft/monitoring-center-components/lib/components/Selector/VersionSelector/VersionSelector'
import Tooltip from '@mulesoft/anypoint-components/lib/Tooltip'
import './BasicAlertModalConfigurations.css'

export type ValueProps = {
  organizationId: string
  existingAlert?: BasicAlert
  servers: ARMServer[]
  serversLoading: boolean
  environmentSelector: EnvironmentSelectorState
  resourceSelector: ResourceSelectorState
  versionSelector: VersionSelectorState
  profile?: Profile
  users: User[]
  emailTemplateEnabled: boolean
  apiAlertsEnabled: boolean
  apiResponseCodesLimit: number
  hasEditRole?: boolean
  isBasicAlertsLimitExceeded: boolean
}

export type EventProps = {
  load: (envId?: string, resourceId?: string, versionId?: string, targetId?: string, resType?: ResourceType) => void
  fetchUsers: (search: string, limit: number, offset: number) => Promise<{ data: User[] }>
  changeUsers: (users: User[]) => void
  environmentSelected: (environmentId: string) => void
  resourceSelected: (resourceId: string, resourceType: ResourceType) => void
  versionSelected: (versionId: string) => void
  saveBasicAlert: (alert: BasicAlert) => void
  deleteBasicAlert: (alert: BasicAlert) => void
  handleClose: () => void
  cleanResourcesSelectors: () => void
}

export type Props = ValueProps & EventProps

export type State = {
  alert: BasicAlert
  selectedServerId?: string
  selectedRecipients?: BasicSelectorOption[]
}

export default class BasicAlertModalConfigurations extends React.Component<Props, State> {

  private basicAlertsLimitTooltipText = 'You have reached the number of application & server alerts allowed for your current plan'

  private applicationMetrics = [
    {value: BasicAlertsMetricType.MessageCount, label: 'Message count'},
    {value: BasicAlertsMetricType.MessageErrorCount, label: 'Message error count'},
    {value: BasicAlertsMetricType.MessageResponseTime, label: 'Message response time'}
  ]

  private serverMetrics = [
    {value: BasicAlertsMetricType.CpuUtilization, label: 'Cpu utilization'},
    {value: BasicAlertsMetricType.MemoryUtilization, label: 'Memory utilization'},
    {value: BasicAlertsMetricType.ThreadCount, label: 'Thread count'}
  ]

  private apiMetrics = [
    {value: BasicAlertsMetricType.APIRequestCount, label: 'Total Request Count'},
    {value: BasicAlertsMetricType.APIResponseTime, label: 'Average Response Time'},
    {value: BasicAlertsMetricType.APIResponseCodes, label: 'Response Codes'},
    {value: BasicAlertsMetricType.APIPolicyViolation, label: 'Policy Violation'}
  ]

  public static bodyDefault(resourceType: string, resourceText: string, alertSource?: string, metricType?: string): string[] {
    const source = alertSource ? alertSource : 'resource'
    return ['Hello,',
      'You are receiving this alert because:',
      BasicAlertModalConfigurations.bodyActualValueDefault(resourceType, resourceText, source, metricType),
      `The ${source} has reached the threshold based on \${condition} defined by alert condition of \${operator} \${threshold} for \${period}.`,
      '',
      `Environment: \${environment}`,
      `\${dashboardLink}`,
      `\${alertLink}`
    ]
  }

  public static bodyActualValueDefault(resourceType: string, resourceText: string, source: string, metricType?: string): string {
    if (resourceType === AlertResourceType.API && metricType === BasicAlertsMetricType.APIResponseCodes) {
      return `The ${source} \${api} has \${value} occurrences with response codes (\${response_codes}) at \${timestamp}.`
    } else {
      return `The ${source} ${resourceText} has \${condition} of \${value} at \${timestamp}.`
    }
  }

  constructor(props: Props) {
    super(props)

    const stateAlert = props.existingAlert || this.buildBasicAlert()
    const alertNotification = getAlertNotification(stateAlert, 'email')
    const selectedRecipients = alertNotification && getSelectedRecipients(props.users, alertNotification.recipients)

    this.state = {
      alert: {...stateAlert},
      selectedServerId: undefined,
      selectedRecipients: selectedRecipients
    }

    this.buildBasicAlert = this.buildBasicAlert.bind(this)
    this.buildEmailNotification = this.buildEmailNotification.bind(this)
    this.getAlertResource = this.getAlertResource.bind(this)
    this.setAggregationFunction = this.setAggregationFunction.bind(this)
    this.getResolvedEmailNotification = this.getResolvedEmailNotification.bind(this)
    this.handleFormChange = this.handleFormChange.bind(this)
    this.handleEmailNotificationChange = this.handleEmailNotificationChange.bind(this)
    this.onChangeRecipients = this.onChangeRecipients.bind(this)
    this.getUsersWrapper = this.getUsersWrapper.bind(this)
    this.onChangeSeverity = this.onChangeSeverity.bind(this)
    this.onConditionChange = this.onConditionChange.bind(this)
    this.environmentSelected = this.environmentSelected.bind(this)
    this.getEnvironmentProps = this.getEnvironmentProps.bind(this)
    this.resourceSelected = this.resourceSelected.bind(this)
    this.versionSelected = this.versionSelected.bind(this)
    this.getResourceProps = this.getResourceProps.bind(this)
    this.getServersProps = this.getServersProps.bind(this)
    this.getConditionProps = this.getConditionProps.bind(this)
    this.getAlertMetrics = this.getAlertMetrics.bind(this)
    this.isRecipientFieldRequired = this.isRecipientFieldRequired.bind(this)
    this.missingRequiredFields = this.missingRequiredFields.bind(this)
    this.handleDeleteAlert = this.handleDeleteAlert.bind(this)
    this.handleCloseModal = this.handleCloseModal.bind(this)
    this.handleSubmit = this.handleSubmit.bind(this)
  }

  buildBasicAlert(): BasicAlert {
    const {profile, emailTemplateEnabled} = this.props

    return {
      alertType: 'basic',
      alertName: '',
      masterOrganizationId: profile && profile.organization.id,
      organizationId: profile && profile.properties.cs_auth && profile.properties.cs_auth.activeOrganizationId,
      resource: this.getAlertResource(AlertResourceType.Application),
      alertViolationInterval: 5,
      aggregationInterval: 1,
      aggregationFunction: 'sum',
      metricType: BasicAlertsMetricType.MessageCount,
      properties: [
        {name: 'operator', value: 'above'},
        {name: 'threshold', value: '0'}
      ],
      alertNotifications: emailTemplateEnabled ? this.buildEmailNotification('application') : [],
      severity: null
    }
  }

  buildEmailNotification(resourceType: string, recipients?: string[], metricType?: string): EmailNotification[] {
    let resourceText, subjectResourceText, sourceText

    switch (resourceType) {
      case AlertResourceType.ServerGroup:
      case AlertResourceType.Cluster:
        resourceText = `\${memberName} from \${targetName}`
        subjectResourceText = `\${memberName} from \${targetName}`
        break
      case AlertResourceType.Server:
        resourceText = `\${targetName}`
        subjectResourceText = `\${targetName}`
        break
      case AlertResourceType.API:
        sourceText = metricType === BasicAlertsMetricType.APIPolicyViolation ? 'API policy' : 'API'
        resourceText = metricType === BasicAlertsMetricType.APIPolicyViolation ? `\${policy}` : `\${api}`
        subjectResourceText = metricType === BasicAlertsMetricType.APIPolicyViolation ? `\${api} \${policy}` : `\${api}`
        break
      case AlertResourceType.Application:
      default:
        resourceText = `\${resource}`
        subjectResourceText = `\${resource}`
        break
    }

    const notification = BasicAlertModalConfigurations.bodyDefault(resourceType, resourceText, sourceText, metricType).join('\n')
    const subjectTemplate = `\${severity}: ${subjectResourceText} \${condition}`
    return [{recipients: recipients || [], subject: subjectTemplate, message: notification, type: 'email'}]
  }

  getAlertResource(sourceType: AlertResourceType, resourceId?: string, serverId?: string, versionId?: string): AlertResource {
    const {organizationId, environmentSelector, resourceSelector, versionSelector} = this.props
    const selectedEnvironment = environmentSelector.selected
    const policyId = this.state && this.state.alert.resource.policyId
    const responseCodes = this.state && this.state.alert.resource.responseCodes

    if (sourceType === 'application') {
      const resources = resourceSelector.resources
      const paramsSelectedResource = resources && resources.find(res =>
        getResourceId(res) === resourceId && isApplicationResource(res)
      )
      const selectedResource = resourceSelector.selected
      const resolvedResource = paramsSelectedResource ? paramsSelectedResource : selectedResource

      if (resolvedResource && selectedEnvironment) {
        return {
          type: AlertResourceType.Application,
          organizationId: organizationId,
          environmentId: selectedEnvironment.id,
          deploymentType: getAlertResourceDeploymentType(resolvedResource),
          clusterId: getClusterId(resolvedResource),
          appId: getInfluxResourceId(resolvedResource)
        }
      }
    } else if (sourceType === 'server') {
      const paramsSelectedServer = this.props.servers.find(s => s.id.toString() === serverId)
      const selectedServer = this.props.servers.find(s => s.id.toString() === this.state.selectedServerId)
      const resolvedServer = paramsSelectedServer ? paramsSelectedServer : selectedServer

      if (selectedEnvironment && resolvedServer) {
        return {
          type: AlertResourceType.Server,
          organizationId: organizationId,
          environmentId: selectedEnvironment.id,
          deploymentType: AlertResourceDeploymentType.HYBRID,
          clusterId: resolvedServer.id.toString()
        }
      }
    } else { // APIs
      const versions = versionSelector.resources
      const paramsSelectedVersion = versions && versions.find(api => api.id.toString() === versionId)
      const selectedVersion = versionSelector.selected

      const resolvedAPI = paramsSelectedVersion || selectedVersion

      if (resolvedAPI && selectedEnvironment) {
        return {
          type: AlertResourceType.API,
          organizationId: organizationId,
          environmentId: selectedEnvironment.id,
          apiId: resourceId,
          apiVersionId: resolvedAPI.id.toString(),
          policyId: policyId,
          responseCodes: responseCodes
        }
      }
    }

    return getDefaultAlertResource(sourceType, policyId, responseCodes)
  }

  setAggregationFunction(metricType: BasicAlertsMetricType) {
    const metric = basicAlertMetricsMap[metricType]
    const aggregationFunction = metric.aggregation

    this.setState({...this.state, alert: {...this.state.alert, metricType: metricType, aggregationFunction: aggregationFunction}})
  }

  getResolvedEmailNotification(resourceType: string, metricType?: string) {
    let emailNotification = getAlertNotification(this.state.alert, 'email')
    const propsSelectedServer = this.props.servers.find(s => s.id.toString() === this.state.alert.resource.clusterId)
    const resolvedResourceType = propsSelectedServer ? propsSelectedServer.type.toLowerCase() : this.state.alert.resource.type
    const emailTemplate = this.buildEmailNotification(resolvedResourceType, undefined, this.state.alert.metricType)

    if (emailNotification) {
      const isEmailTemplate = emailNotification.subject === emailTemplate[0].subject && emailNotification.message === emailTemplate[0].message
      emailNotification = isEmailTemplate ? this.buildEmailNotification(resourceType, emailNotification.recipients, metricType)[0] : emailNotification
    } else {
      emailNotification = this.buildEmailNotification(resourceType)[0]
    }

    return emailNotification
  }

  handleFormChange(form: any) {
    if (form.target === 'alertName') {
      const alertName = form.value.alertName.replace(/ /g, '')
      this.setState({...this.state, alert: {...this.state.alert, alertName: alertName.length === 0 ? undefined : form.value.alertName}})
    } else if (form.target === 'sourceType') {
      const sourceType = form.value.sourceType
      const selectedResource = this.getAlertResource(sourceType)

      const propsSelectedServer = this.props.servers.find(s => s.id.toString() === selectedResource.clusterId)
      const resolvedResourceType = propsSelectedServer ? propsSelectedServer.type.toLowerCase() : form.value.sourceType
      const emailNotification = this.getResolvedEmailNotification(resolvedResourceType)

      const alertMetrics = this.getAlertMetrics(selectedResource)
      this.setAggregationFunction(alertMetrics[0].value)

      const fields = this.getConditionProps().fields
      this.onConditionChange(fields)

      this.setState({...this.state, alert: {...this.state.alert, resource: selectedResource, metricType: alertMetrics[0].value,
          alertNotifications: [emailNotification]}})
    } else if (form.target === 'alertServer') {
      const selectedServer = this.getAlertResource(AlertResourceType.Server, undefined, form.value.alertServer)
      const propsSelectedServer = this.props.servers.find(s => s.id.toString() === selectedServer.clusterId)
      const targetType = propsSelectedServer ? propsSelectedServer.type.toLowerCase() : 'server'
      const emailNotification = this.getResolvedEmailNotification(targetType)
      const clusterId = selectedServer.clusterId && selectedServer.clusterId.toString()

      this.setState({...this.state, selectedServerId: clusterId, alert: {...this.state.alert, resource: selectedServer,
          alertNotifications: [emailNotification]}})
    } else if (form.target === 'metricType') {
      const metricType = form.value.metricType

      if (this.apiMetrics.find(metric => metric.value === metricType)){
        const emailNotification = this.getResolvedEmailNotification(AlertResourceType.API, metricType)
        this.setState({...this.state, alert: {...this.state.alert, alertNotifications: [emailNotification]}})
      }

      this.setAggregationFunction(metricType)

      const fields = this.getConditionProps().fields
      this.onConditionChange(fields)
    } else if (form.target === 'alertSubject' || form.target === 'alertMessage') {
      this.handleEmailNotificationChange(form.value.alertSubject, form.value.alertMessage, this.state.selectedRecipients)
    }
  }

  onConditionChange(fields: BasicAlertConditionFields) {
    const alert = setAlertCondition(this.state.alert, fields)
    this.setState({...this.state, alert})
  }

  trimRecipientsWhiteSpace(alertRecipients?: BasicSelectorOption[]){
    return alertRecipients ? alertRecipients.map(elem => {
      return {label: elem.label.trim(), value: elem.value.trim()}
    }) : undefined;
  }

  handleEmailNotificationChange(alertSubject: string, alertMessage: string, alertRecipients?: BasicSelectorOption[]) {
    const subject = alertSubject
    const message = alertMessage
    // removing white spaces
    const trimmedRecipients = this.trimRecipientsWhiteSpace(alertRecipients)
    const uniqueRecipients = trimmedRecipients ? trimmedRecipients.filter((value, index, array) => {
      return array.map(elem => elem.value).indexOf(value.value) === index
    }) : undefined
    const recipients = uniqueRecipients ? uniqueRecipients.map(emailOpt => emailOpt.value) : []

    let emailNotification = getAlertNotification(this.state.alert, 'email')

    if (emailNotification) {
      emailNotification.subject = subject
      emailNotification.message = message
      emailNotification.recipients = recipients

      const alertNotifications = this.state.alert.alertNotifications.filter(notification => notification.type !== 'email')
      const newAlertNotifications = alertNotifications && alertNotifications.concat(emailNotification)

      this.setState({...this.state, alert: {...this.state.alert, alertNotifications: newAlertNotifications}, selectedRecipients: uniqueRecipients})
    } else {
      const newEmailNotification: EmailNotification = {
        type: 'email',
        subject: subject,
        message: message,
        recipients: recipients
      }

      const alertNotifications = this.state.alert.alertNotifications
      const newAlertNotifications = alertNotifications && alertNotifications.concat(newEmailNotification)

      this.setState({...this.state, alert: {...this.state.alert, alertNotifications: newAlertNotifications}, selectedRecipients: uniqueRecipients})
    }
  }

  onChangeRecipients(e: { value: any }) {
    const emailNotification = getAlertNotification(this.state.alert, 'email')

    if (emailNotification) {
      this.handleEmailNotificationChange(emailNotification.subject, emailNotification.message, e.value)
    } else {
      this.handleEmailNotificationChange('', '', e.value)
    }
  }

  getUsersWrapper(qr: { query: string, limit: number, offset: number }) {
    return new Promise((resolve) => {
      this.props.fetchUsers(qr.query, qr.limit, qr.offset).then((result: { data: User[] }) => {
        const users = result.data
        this.props.changeUsers(users)

        const emails = this.state.selectedRecipients && this.state.selectedRecipients.map(recipient => recipient.value)
        this.setState({...this.state, selectedRecipients: emails && getSelectedRecipients(users.concat(this.props.users), emails)})

        const emailOptions = users.map(user => getUserEmailOption(user))
        const filteredOptions = emails ? emailOptions.filter(opt => emails.indexOf(opt.value) < 0) : emailOptions
        resolve(filteredOptions)
      }).catch(() => resolve([]))
    })
  }

  onChangeSeverity(e: { value: AlertSeverity }) {
    this.setState({...this.state, alert: {...this.state.alert, severity: e.value}})
  }

  environmentSelected(envId: string) {
    const emailNotification = this.getResolvedEmailNotification(this.state.alert.resource.type)
    this.props.environmentSelected(envId)

    this.setState({...this.state, selectedServerId: undefined}, () => {
      const selectedApplication = this.getAlertResource(this.state.alert.resource.type)
      this.setState({...this.state, alert: {...this.state.alert, resource: selectedApplication, alertNotifications: [emailNotification]}})
    })
  }

  getEnvironmentProps() {
    const {environmentSelector, existingAlert} = this.props
    return {
      ...transformToEnvironmentSelectorProps(environmentSelector, 45, undefined, !!existingAlert),
      environmentSelected: this.environmentSelected
    }
  }

  resourceSelected(resourceId: string, resourceType: ResourceType) {
    this.props.resourceSelected(resourceId, resourceType)

    const resType = resourceType !== ResourceType.MANAGED_API && resourceType !== ResourceType.API ?
      AlertResourceType.Application : AlertResourceType.API
    const resource = this.getAlertResource(resType, resourceId)
    this.setState({...this.state, alert: {...this.state.alert, resource}})
  }

  versionSelected(versionId: string) {
    this.props.versionSelected(versionId)
    const selectedAPI = this.getAlertResource(AlertResourceType.API, undefined, undefined, versionId)

    this.setState({...this.state, alert: {...this.state.alert, resource: selectedAPI}})
  }

  getResourceProps() {
    const {environmentSelector, resourceSelector, existingAlert, isBasicAlertsLimitExceeded} = this.props
    const isAPIAlert = this.state.alert.resource.type === 'api'
    const disabled = !environmentSelector.selected || !!existingAlert || (isBasicAlertsLimitExceeded && !isAPIAlert)

    const resourcesFilter = (r: SelectorResource): Boolean => {
      if (isAPIAlert) return r.resourceType === ResourceType.API || r.resourceType === ResourceType.MANAGED_API
      else return r.resourceType !== ResourceType.API && r.resourceType !== ResourceType.MANAGED_API
    }

    const props =  {
      ...transformToResourceSelectorProps(resourceSelector, 52, undefined, disabled),
      resourceSelected: this.resourceSelected
    }

    const resources = props.resources && props.resources.filter(resourcesFilter)
    const placeholder = !isAPIAlert ? undefined : (resources && resources.length > 0 ? 'Pick an API' : 'No APIs in this environment')

    return {...props, resources, placeholder}
  }

  getVersionProps() {
    const {resourceSelector, versionSelector, existingAlert} = this.props
    const disabled = !resourceSelector.selected || !isAsset(resourceSelector.selected) || !!existingAlert

    return {
      ...transformToVersionSelectorProps(versionSelector, 52, undefined, disabled),
      versionSelected: this.versionSelected,
      supportHTTPMetrics: true
    }
  }

  getServersProps() {
    const {environmentSelector, existingAlert, serversLoading, servers, isBasicAlertsLimitExceeded} = this.props
    const resolvedServers = HybridSettingsPage.getResolvedServers(servers)
    return {
      placeholder: serversLoading ? 'Loading...' :
        resolvedServers.length > 0 ? 'Pick a server / server group / cluster' : 'No servers in this environment',
      dropdownHint: serversLoading ? 'We are still loading your resources...' : 'Only showing servers with Anypoint Monitoring enabled.',
      optionRenderer: HybridSettingsPage.renderedResource,
      valueRenderer: HybridSettingsPage.renderedResource,
      testId: 'basic-alert-modal-body-server-select',
      className: 'hybrid-server-select',
      disabled: !environmentSelector.selected || !!existingAlert || isBasicAlertsLimitExceeded,
      isLoading: serversLoading,
      options: resolvedServers,
      name: 'alertServer'
    }
  }

  getConditionProps() {
    const {alert} = this.state
    const {environmentSelector} = this.props
    const operatorProp = getAlertProperty(alert, 'operator')
    const thresholdProp = getAlertProperty(alert, 'threshold')
    const metric = basicAlertMetricsMap[alert.metricType]

    return {
      onChange: this.onConditionChange,
      fields: {
        operator: operatorProp && operatorProp.value,
        threshold: thresholdProp ? getMetricThreshold(alert.metricType, thresholdProp) : '0',
        window: alert.alertViolationInterval.toString(),
        orgId: alert.resource.organizationId,
        envId: alert.resource.environmentId || (environmentSelector.selected && environmentSelector.selected.id),
        appId: alert.resource.appId,
        apiId: alert.resource.apiVersionId,
        policyId: alert.resource.policyId,
        responseCodes: alert.resource.responseCodes && alert.resource.responseCodes.join(','),
        editDisabled: !this.props.hasEditRole
      },
      getValidMinThreshold: metric.getValidMinThreshold
    }
  }

  getAlertMetrics(alertResource: AlertResource) {
    if (alertResource.type === 'application') {
      const deploymentType = alertResource.deploymentType && alertResource.deploymentType.toUpperCase()

      if (deploymentType === AlertResourceDeploymentType.CLOUDHUB || deploymentType === AlertResourceDeploymentType.RTF) {
        return this.applicationMetrics.concat(this.serverMetrics)
      } else {
        return this.applicationMetrics
      }
    } else if (alertResource.type === 'server') {
      return this.serverMetrics
    } else {
      return this.apiMetrics
    }
  }

  isRecipientFieldRequired() {
    const emailNotification = getAlertNotification(this.state.alert, 'email')
    const hasRecipients = emailNotification && emailNotification.recipients && emailNotification.recipients.length > 0
    return emailNotification && emailNotification.subject && emailNotification.message && hasRecipients
  }

  missingRequiredFields() {
    const alert = this.state.alert
    const alertName = alert.alertName
    const alertSeverity = alert.severity
    const hasResource = alert.resource.type === 'api' ? !!alert.resource.apiVersionId : !!alert.resource.clusterId
    const metricType = alert.metricType
    const isMetricValid = basicAlertMetricsMap[metricType].isBasicAlertValid(alert)
    const operatorProperty = getAlertProperty(alert, 'operator')
    const operatorValue = operatorProperty && operatorProperty.value
    const thresholdProperty = getAlertProperty(alert, 'threshold')
    const thresholdValue = thresholdProperty && thresholdProperty.value
    const alertViolationInterval = 5 <= alert.alertViolationInterval && alert.alertViolationInterval <= 20
    const isRecipientFieldRequired = this.isRecipientFieldRequired()

    return !(alertName && alertSeverity && hasResource && isMetricValid && metricType && operatorValue && alertViolationInterval &&
      isRecipientFieldRequired && !!thresholdValue)
  }

  handleDeleteAlert(event: any) {
    if (!event.target.type && this.props.existingAlert) {
      this.props.deleteBasicAlert(this.props.existingAlert)
      this.props.handleClose()
    }
  }

  handleCloseModal(event: any) {
    if (!event.target.type) {
      this.props.handleClose()
    }
  }

  handleSubmit() {
    if (!this.missingRequiredFields()) {
      let newAlert = this.state.alert
      newAlert.alertName = this.state.alert.alertName.trim()

      this.props.saveBasicAlert(newAlert)
      this.props.handleClose()
    }
  }

  componentWillMount() {
    const {existingAlert, environmentSelector, load} = this.props

    if (existingAlert) {
      const alertResource = existingAlert.resource
      let resourceId, resType

      switch (existingAlert.resource.type) {
        case AlertResourceType.Server:
        case AlertResourceType.ServerGroup:
        case AlertResourceType.Cluster:
          resourceId = alertResource.clusterId
          break
        case AlertResourceType.API:
          resType = ResourceType.API
          resourceId = alertResource.apiId
          break
        default:
          resourceId = alertResource.appId
      }

      load(alertResource.environmentId, resourceId, alertResource.apiVersionId, alertResource.clusterId, resType)
    } else if (environmentSelector.selected) {
      load(environmentSelector.selected.id)
    } else {
      load()
    }
  }

  componentWillUnmount() {
    this.props.cleanResourcesSelectors()
  }

  render() {
    const {existingAlert, apiAlertsEnabled, apiResponseCodesLimit, isBasicAlertsLimitExceeded} = this.props
    const {alert, selectedRecipients} = this.state

    const metrics = this.getAlertMetrics(alert.resource)
    const environmentProps = this.getEnvironmentProps()
    const resourceProps = this.getResourceProps()
    const serversProps = this.getServersProps()
    const versionProps = this.getVersionProps()
    const emailNotification = getAlertNotification(alert, 'email')
    const editDisabled = !this.props.hasEditRole

    const metric = basicAlertMetricsMap[alert.metricType]
    const metricConditionProps = {...this.getConditionProps(), apiResponseCodesLimit}

    const values = {
      alertName: alert.alertName,
      alertSeverity: alert.severity ? alert.severity : undefined,
      alertServer: alert.resource.clusterId,
      sourceType: alert.resource.type,
      metricType: alert.metricType,
      alertSubject: emailNotification && emailNotification.subject,
      alertMessage: emailNotification && emailNotification.message,
      alertRecipients: selectedRecipients
    }

    const saveButton = (
      <Button
        kind="primary"
        onClick={this.handleSubmit}
        testId={'basic-alert-modal-save-button'}
        disabled={this.missingRequiredFields() || editDisabled}
      >
        {existingAlert ? 'Save' : 'Create'}
      </Button>
    )

    const saveButtonWithToolTip = editDisabled ? (
      <Tooltip
        content={labels.missingAlertPermissions}
        position={'top'}
      >
        {saveButton}
      </Tooltip>
    ) : saveButton

    const deleteButton = (
      <Button
        kind="danger"
        onClick={this.handleDeleteAlert}
        noFill={true}
        testId={'basic-alert-modal-delete-alert-button'}
        disabled={editDisabled}
      >
        Delete alert
      </Button>
    )

    const deleteButtonWithToolTip = editDisabled ? (
      <Tooltip
        content={labels.missingAlertPermissions}
        position={'top'}
      >
        {deleteButton}
      </Tooltip>
    ) : deleteButton

    const radioButtons = [
      {label: 'Application', value: 'application', isVisible: true},
      {label: 'Server', value: 'server', isVisible: true},
      {label: 'API', value: 'api', isVisible: apiAlertsEnabled}
    ]

    return (
      <Form
        testId={'selector-form'}
        onChange={this.handleFormChange}
        value={values}
      >
        <div className={'basic-alert-modal-body-section'}>
          <div className={'basic-alert-modal-body-input'}>
            <span>Alert name</span>
            <TextField
              testId={'basic-alert-modal-body-alert-name'}
              placeholder={'Alert name'}
              value={alert.alertName}
              name="alertName"
              maxLength={'40'}
              disabled={editDisabled}
            />
          </div>
          <div className={'basic-alert-modal-body-input'}>
            <span>Severity level</span>
            <SeveritySelector
              className="monitoring-severity-selector"
              name="alertSeverity"
              onChange={this.onChangeSeverity}
              testId={'basic-alert-modal-body-alert-severity'}
              value={values.alertSeverity}
              disabled={editDisabled}
            />
          </div>
        </div>

        <div className={'basic-alert-modal-body-section'}>
          <div className={'basic-alert-modal-body-section-title'}>
            <span>Alert source</span>
          </div>
          <div className={'basic-alert-modal-body-radio-group'}>
            <RadioGroup testId={'basic-alert-modal-source-type'} label="Source type" name="sourceType" required={true} disabled={!!existingAlert}>
              {radioButtons.filter(t => t.isVisible).map(r => (
                <Radio key={r.value} label={r.label} value={r.value}/>
              ))}
            </RadioGroup>
          </div>
          {environmentProps && <div className={'basic-alert-modal-body-input'}>
            <span>Environment name</span>
            <EnvironmentSelector {...environmentProps} testId={'basic-alert-modal-body-environment-select'}/>
          </div>}
          {alert.resource.type !== 'server' && resourceProps &&
          <div className={`basic-alert-modal-body-input${existingAlert ? ' resource-container-disabled' : ''}`}>
            <span>{alert.resource.type === 'api' ? 'API' : 'Resource name'}</span>
            <ResourceSelector {...resourceProps} testId={'basic-alert-modal-body-application-select'}/>
          </div>}
          {alert.resource.type === 'api' &&
          <div className={`basic-alert-modal-body-input${existingAlert ? ' resource-container-disabled' : ''}`}>
            <span>Version/Instance</span>
            <VersionSelector {...versionProps} testId={'basic-alert-modal-body-version-select'}/>
          </div>}
          {alert.resource.type === 'server' && <div className={'basic-alert-modal-body-input'}>
            <span>Server name</span>
            <Select {...serversProps}/>
          </div>}
          {isBasicAlertsLimitExceeded && alert.resource.type !== 'api' && <div className={'basic-alert-modal-body-error-message'}>
            <Tooltip testId={'new-alert-dropdown-basic-alerts-limit-tooltip'} content={this.basicAlertsLimitTooltipText} position={'bottom'}>
              <span className={'new-alert-dropdown-basic-alerts-limit-tag'}>Application and server alerts limit reached</span>
            </Tooltip>
          </div>}
        </div>

        <div className={'basic-alert-modal-body-section'}>
          <div className={'basic-alert-modal-body-section-title'}>
            <span>Alert condition</span>
          </div>
          <div className={'basic-alert-modal-body-input'}>
            <span>Metric</span>
            <Select testId={'basic-alert-modal-body-metric-select'} options={metrics} name="metricType" disabled={editDisabled}/>
          </div>
          {metric.condition(metricConditionProps)}
        </div>

        <div className={'basic-alert-modal-body-section'}>
          <div className={'basic-alert-modal-body-section-title email-notifications-title'}>
            <span>Email notifications</span>
          </div>
          <div className={'basic-alert-modal-body-section-subtitle'}>
            <span>Use variables to include information about your resource in the Subject and Message. </span>
            <a href={'https://docs.mulesoft.com/monitoring/basic-alerts#alert-variables'} target={'_blank'}>Learn more</a>
          </div>
          <div className={'basic-alert-modal-body-input'}>
            <span>Subject</span>
            {/* tslint:disable-next-line:max-line-length */}
            <TextField testId={'basic-alert-modal-body-input-subject'} placeholder={'Email subject'} name="alertSubject" maxLength={'100'} disabled={editDisabled}/>
          </div>
          <div className={'basic-alert-modal-body-input'}>
            <span>Message</span>
            {/* tslint:disable-next-line:max-line-length */}
            <TextArea testId={'basic-alert-modal-body-input-message'} placeholder={'Custom message'} name="alertMessage" maxLength={'2000'} disabled={editDisabled}/>
          </div>
          <div className={'basic-alert-modal-body-input'}>
            <span>Recipients</span>
            <RecipientsSelector
              className="monitoring-recipients-selector"
              name="alertRecipients"
              optionsQuery={this.getUsersWrapper}
              onChange={this.onChangeRecipients}
              testId={'basic-alert-modal-body-alert-recipients'}
              value={values.alertRecipients}
              disabled={editDisabled}
            />
          </div>
        </div>

        <div data-test-id={existingAlert ? 'selector-form-buttons-footer-existing-alert' : 'selector-form-buttons-footer'}>
          {existingAlert && deleteButtonWithToolTip}
          <div data-test-id="selector-form-buttons-footer-close-save">
            <Button kind="tertiary" onClick={this.handleCloseModal} noFill={true} testId={'basic-alert-modal-cancel-button'}>
              {existingAlert ? 'Close' : 'Cancel'}
            </Button>
            {saveButtonWithToolTip}
          </div>
        </div>
      </Form>
    )
  }
}
